/**
 * cl-core/siblings — naver-rank R4 형제 이름 감쇠(2026-10-25). 순수 TS.
 *
 * 같은 시군구 정비사업 표·「함께 보는 현장」 카드에서 형제 현장 이름은 «링크로 한 번만» 나온다.
 *   · 이 현장 이름과 같은 행은 뺀다(자기 이름이 형제 칸에 다시 나오지 않게).
 *   · 이미 다른 목록(먼저 그린 쪽)에 나온 이름은 다시 싣지 않는다(공백 무시 대조).
 *   · 제목(h2/h3)·첫 문단에는 형제 이름을 쓰지 않는다 — 카드 제목은 호출부가 p 로 낸다(SiteCard as="p").
 */

import { redevZoneLabel, splitDistrict } from './title';

function key(s: string | null | undefined): string {
  return String(s ?? '').replace(/\s+/g, '').normalize('NFC').toLowerCase();
}

/** rows 중 taken(이미 나온 이름·자기 이름)과 겹치거나 서로 겹치는 이름을 뺀다. 순서는 유지. */
export function dedupeSiblingRows<T extends { name: string; slug?: string | null }>(rows: ReadonlyArray<T>, taken: ReadonlyArray<string | null | undefined> = []): T[] {
  const seen = new Set(taken.map(key).filter(Boolean));
  const seenSlug = new Set<string>();
  const out: T[] = [];
  for (const r of rows) {
    const k = key(r?.name);
    if (!k || seen.has(k)) continue;
    const sk = key(r?.slug ?? '');
    if (sk && seenSlug.has(sk)) continue;
    seen.add(k);
    if (sk) seenSlug.add(sk);
    out.push(r);
  }
  return out;
}

/* ───────── naver-rank R6 — 블로그 제목 → 현장 해석(렌더 시점 · blog_posts 무변경) ───────── */

export interface SiteNameRow { slug: string; name: string }

const GENERIC_NAME_RE = /^(재개발|재건축|아파트|분양|청약|미분양|정비사업|부산|서울|경남|울산)$/;

/**
 * 글 제목에 현장 이름이 «그대로» 들어 있으면 그 현장 하나를 고른다(가장 긴 이름 · 공백 무시).
 *   · 이름 4자 미만·일반어는 대조하지 않는다. · 숫자로 끝나는 이름은 뒤에 숫자가 붙은 꼴(「괴정1」↔「괴정11」)을 맞히지 않는다.
 *   · 같은 길이의 다른 현장이 둘 이상 맞으면(모호) null — 틀린 현장으로 잇느니 안 잇는다.
 */
export function matchSiteInTitle(title: string | null | undefined, rows: ReadonlyArray<SiteNameRow>): SiteNameRow | null {
  const t = key(title);
  if (!t) return null;
  let best: SiteNameRow | null = null;
  let bestLen = 0;
  let ambiguous = false;
  for (const r of rows) {
    const k = key(r?.name);
    if (!r?.slug || k.length < 4 || GENERIC_NAME_RE.test(k)) continue;
    let at = t.indexOf(k);
    let ok = false;
    while (at >= 0) {
      const next = t.charAt(at + k.length);
      const prev = at > 0 ? t.charAt(at - 1) : '';
      if (!(/[0-9]$/.test(k) && /[0-9]/.test(next)) && !(/^[0-9]/.test(k) && /[0-9]/.test(prev))) { ok = true; break; }
      at = t.indexOf(k, at + 1);
    }
    if (!ok) continue;
    if (k.length > bestLen) { best = r; bestLen = k.length; ambiguous = false; }
    else if (k.length === bestLen && best && best.slug !== r.slug) ambiguous = true;
  }
  return best && !ambiguous ? best : null;
}

/* ───────── seo-detail (2026-10-25) — 번호 이웃 정비구역(구서3 ↔ 구서4 · 괴정11 ↔ 괴정7 · 용호2 ↔ 용호3) ─────────
 *
 * 네이버 실측: 「구서3」 질의가 구서4 상세에, 「괴정11」이 괴정7 에, 「촉진4」가 촉진1 에 착지했다. 같은 동 이름 + 다른 번호의
 * 형제 구역이 서로 링크로 이어져 있으면(정확한 앵커 「구서4구역 재건축」) 검색엔진이 번호를 갈라 볼 재료가 생긴다.
 * 판정은 이름만 본다(같은 시군구 행 목록은 호출부가 넘긴다): 구역 «줄기»(지역 낱말·유형어·「주변재정비/재정비」·공백 제거 뒤
 * 번호 앞 한글)가 같고 번호가 다른 현장. 번호 거리 순 · 상한 8.
 */

export interface ZoneParts { stem: string; num: string; numValue: number }

const ZONE_RE = /^([가-힣]{1,14}?)(\d+(?:-\d+)?|[A-Za-z])(?:구역)?(?=[가-힣(]|$)/;

/** 이름 → 구역 줄기·번호. 번호가 없으면 null. 괄호 안 구역명(「호반써밋 초량(초량3구역)」)이 있으면 그쪽을 본다. */
export function zoneParts(name: string | null | undefined, sigungu?: string | null): ZoneParts | null {
  const sp = splitDistrict(String(name ?? ''));
  const raw = sp.district && /[0-9A-Za-z]/.test(sp.district) ? sp.district : sp.name;
  const z = redevZoneLabel(raw, sigungu).replace(/\s+/g, '').replace(/주변재정비|재정비/g, '');
  const m = ZONE_RE.exec(z);
  if (!m) return null;
  const num = m[2].toUpperCase();
  const head = Number(num.split('-')[0]);
  const numValue = Number.isFinite(head) ? head + (num.includes('-') ? Number(num.split('-')[1]) / 100 : 0) : 100 + num.charCodeAt(0);
  return { stem: m[1], num, numValue };
}

const TYPE_WORD_RE = /소규모재건축|가로주택정비|재건축|재개발/;

/** 형제 링크 앵커: 「구서4구역 재건축」 · 브랜드가 있으면 「사직3구역 재개발(자이 엘리스트)」. */
export function siblingZoneAnchor(name: string, sigungu?: string | null): string {
  const p = zoneParts(name, sigungu);
  const sp = splitDistrict(String(name ?? ''));
  const type = (String(name ?? '').replace(/\s+/g, '').match(TYPE_WORD_RE) ?? [])[0] ?? '재개발';
  if (!p) return String(name ?? '').replace(/\s+/g, ' ').trim();
  // 표기는 원문 구역명(「시민공원주변재정비촉진1구역」)을 살린다 — 줄기(비교용 축약)를 앵커에 쓰지 않는다.
  const raw = sp.district && /[0-9A-Za-z]/.test(sp.district) ? sp.district : sp.name;
  let zone = redevZoneLabel(raw, sigungu).replace(new RegExp(`\\s*(?:${TYPE_WORD_RE.source})\\s*`, 'g'), ' ').replace(/\s+/g, ' ').trim();
  if (/재개발|재건축/.test(type) && /[0-9A-Za-z]$/.test(zone)) zone = `${zone}구역`;
  if (!/재개발|재건축/.test(type)) zone = zone.replace(/구역$/, '') || zone;
  const brand = sp.district && /[0-9A-Za-z]/.test(sp.district) && !TYPE_WORD_RE.test(sp.name) ? sp.name : null;
  return brand ? `${zone} ${type}(${brand})` : `${zone} ${type}`;
}

export interface SiblingZoneRow { slug: string; anchor: string; num: string }

export const SIBLING_ZONE_MAX = 8;

/** 같은 시군구 행 목록 → 이 현장의 번호 이웃 구역(자기·집계 행 제외 · 번호 거리 순). 이 현장 번호가 없으면 []. */
export function numberedSiblingZones(
  self: { slug: string; name: string | null | undefined },
  rows: ReadonlyArray<{ slug?: string | null; name?: string | null; is_aggregate?: boolean | null }>,
  sigungu?: string | null,
  max = SIBLING_ZONE_MAX,
): SiblingZoneRow[] {
  const me = zoneParts(self.name, sigungu);
  if (!me) return [];
  const selfKey = key(self.slug);
  const out: Array<SiblingZoneRow & { d: number }> = [];
  const seen = new Set<string>();
  for (const r of rows ?? []) {
    const slug = String(r?.slug ?? '').trim();
    const name = String(r?.name ?? '').trim();
    if (!slug || !name || r?.is_aggregate || key(slug) === selfKey || seen.has(key(slug))) continue;
    const p = zoneParts(name, sigungu);
    if (!p || p.stem !== me.stem) continue;
    seen.add(key(slug));
    out.push({ slug, anchor: siblingZoneAnchor(name, sigungu), num: p.num, d: Math.abs(p.numValue - me.numValue) });
  }
  out.sort((a, b) => a.d - b.d || a.num.localeCompare(b.num, 'ko'));
  return out.slice(0, max).map(({ d: _d, ...r }) => r);
}
